// DistanceBadge - Shows distance and walking time to a place

import React from 'react';
import { MapPin, Navigation } from 'lucide-react';
import type { Coordinates } from '../../utils/distance';
import { getDistanceBadge } from '../../utils/distance';

interface DistanceBadgeProps {
  userLocation: Coordinates | null;
  placeLocation: Coordinates | null;
  variant?: 'compact' | 'full';
  className?: string;
}

export function DistanceBadge({
  userLocation,
  placeLocation,
  variant = 'compact',
  className = '',
}: DistanceBadgeProps) {
  const distanceInfo = getDistanceBadge(userLocation, placeLocation);

  if (!distanceInfo) {
    return null;
  }

  if (variant === 'compact') {
    return (
      <span
        className={`inline-flex items-center gap-1 text-xs text-gray-500 ${className}`}
      >
        <MapPin className="w-3 h-3" />
        {distanceInfo.distance}
      </span>
    );
  }

  return (
    <div
      className={`flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 rounded-lg ${className}`}
    >
      <Navigation className="w-4 h-4 text-indigo-600" />
      <div className="flex flex-col">
        <span className="text-sm font-medium text-gray-900">
          {distanceInfo.distance}
        </span>
        <span className="text-xs text-gray-500">
          {distanceInfo.walkingTime}
        </span>
      </div>
    </div>
  );
}

// Inline version for cards
export function DistanceText({
  userLocation,
  placeLocation,
}: {
  userLocation: Coordinates | null;
  placeLocation: Coordinates | null;
}) {
  const distanceInfo = getDistanceBadge(userLocation, placeLocation);

  if (!distanceInfo) {
    return null;
  }

  // Show walking time for nearby places, distance for far ones
  if (distanceInfo.distanceKm < 3) {
    return (
      <span className="text-xs text-indigo-600 font-medium">
        {distanceInfo.walkingTime}
      </span>
    );
  }

  return (
    <span className="text-xs text-gray-500">
      {distanceInfo.distance}
    </span>
  );
}

export default DistanceBadge;
